import type { LocationQuery } from '#vue-router';

type Config = {
  url?: string;
  iframeElement?: HTMLIFrameElement | null;
  behavior?: {
    redirect?: 'on' | 'off' | 'force';
    postMessage?: 'on' | 'off' | 'force';
  };
};

const defaultConfig: Config = {
  url: '',
  iframeElement: null,
  behavior: {
    redirect: 'off',
    postMessage: 'force',
  },
};

const DEFAULT_HOMEPAGE: CactusSportsbook.DeepLink = {
  eventType: 'tab',
  eventData: {
    value: 'Upcoming',
  },
};

type GoToPageReturnObject = {
  iframeUrl: string;
  navigationObject: CactusSportsbook.DeepLink;
};

const ROUTE_QUERY_ENTRIES: CactusSportsbook.PathEntryType[] = [
  'bscode',
  'eventId',
  'leagueId',
  'sportId',
  'masterLeagueId',
  'selectionId',
  'eventlistTab',
  'betting-history',
  'open-bets',
];

export class CactusSportsbook {
  private static instance: CactusSportsbook;
  private config: Config;

  public static getInstance(): CactusSportsbook {
    if (!CactusSportsbook.instance) {
      CactusSportsbook.instance = new CactusSportsbook();
    }

    return CactusSportsbook.instance;
  }

  constructor(config?: Config) {
    this.config = config || defaultConfig;
  }

  /**
   * Is token anonymous or logged
   * @returns boolean
   */
  public get isAuthenticated(): boolean {
    if (!this.urlData) {
      return false;
    }

    const { token } = this.urlData;

    return token !== 'logout';
  }

  /**
   * Returns base url
   * @returns string
   */
  public get baseUrl(): string {
    const { url } = this.config;

    return url ?? '';
  }

  /**
   * Has url set-up
   * @returns boolean
   */
  public get hasUrlSetup(): boolean {
    return !!this.baseUrl;
  }

  /**
   * Updates instance configs
   * @param config Config
   * @returns void
   */
  public updateConfig(config: Config): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Redirect to url.
   * @param url target location
   * @returns void
   */
  private redirectTo(url: string): void {
    window.location.assign(url);
  }

  /**
   * Get url partials
   * @returns UrlData | null
   */
  public get urlData(): CactusSportsbook.UrlData | null {
    const { url } = this.config;

    if (!url) {
      return null;
    }

    const { origin, pathname, searchParams } = new URL(url);

    const params = new URLSearchParams(searchParams);
    const token = params.get('operatorToken') || '';

    const [language, path] = pathname.split('/').splice(-2);

    return {
      origin,
      language: language || '',
      path: path || '',
      token,
    };
  }

  /**
   * Get correct path from route query
   * @param queryParams
   * @returns string
   */
  public discoveryPathFromQueryParams(
    queryParams: LocationQuery,
  ): CactusSportsbook.PathEntry {
    const entries = objectEntries(queryParams);

    const defaultReturn = {
      [DEFAULT_HOMEPAGE.eventType]: (DEFAULT_HOMEPAGE.eventData as any).value,
    } as CactusSportsbook.PathEntry;

    if (!entries.length) {
      return defaultReturn;
    }

    const sportsbookParam = entries.find(([eventType]: [any, any]) => {
      return ROUTE_QUERY_ENTRIES.includes(
        eventType as CactusSportsbook.PathEntryType,
      );
    });

    if (!sportsbookParam) {
      return defaultReturn;
    }

    const [eventType, eventDataValue] = sportsbookParam as any;

    return {
      [eventType]: eventDataValue,
    } as CactusSportsbook.PathEntry;
  }

  /**
   * Get correct path from Location Change event
   * @param entryData
   * @returns string
   */
  public discoveryPathFromLocationChangeEvent(
    entryData: Record<string, any>,
  ): LocationQuery | null {
    const defaultReturn = {};

    if (!entryData) {
      return defaultReturn;
    }

    if (entryData.eventID) {
      return {
        eventId: entryData.eventID,
      };
    }

    if (entryData.leagueID) {
      return {
        leagueId: entryData.leagueID,
      };
    }

    if (entryData.branchID) {
      return {
        sportId: entryData.branchID,
      };
    }

    return defaultReturn;
  }

  /**
   * Sanitize the query params removing Sportsbook specific params
   * @param queryParams
   * @returns LocationQuery
   */
  public sanitizeQueryParams(queryParams: LocationQuery): LocationQuery {
    const sanitizedParams: LocationQuery = {};

    for (const [key, value] of Object.entries(queryParams)) {
      if (ROUTE_QUERY_ENTRIES.includes(key as CactusSportsbook.PathEntryType)) {
        continue;
      }

      sanitizedParams[key] = value;
    }

    return sanitizedParams;
  }

  /**
   * Generate a valid object to navigate in sportsbook
   * @param pathEntry
   * @returns string
   */
  private createNavigateObject(
    pathEntry: CactusSportsbook.PathEntry,
  ): CactusSportsbook.DeepLink {
    if (!pathEntry) {
      return DEFAULT_HOMEPAGE;
    }

    const entries = objectEntries(pathEntry);

    if (!entries.length) {
      return DEFAULT_HOMEPAGE;
    }

    const [eventType, eventDataValue] = entries[0] as [
      CactusSportsbook.PathEntryType,
      string | boolean | undefined,
    ];

    return {
      eventType,
      eventData: {
        value: eventDataValue,
      },
    };
  }

  /**
   * Generate a valid url to sportsbook
   * @param pathEntry
   * @returns string
   */
  private createRedirectUrl(pathEntry: CactusSportsbook.PathEntry): string {
    if (!pathEntry) {
      return this.baseUrl;
    }

    const entries = objectEntries(pathEntry);

    if (!entries.length) {
      return this.baseUrl;
    }

    const [eventType, eventDataValue] = entries[0] as [
      CactusSportsbook.PathEntryType,
      string | boolean | undefined,
    ];

    if (['betting-history', 'open-bets'].includes(eventType)) {
      return `${this.baseUrl}#${eventType}`;
    }

    if (eventType === 'eventlistTab') {
      return `${this.baseUrl}#${eventDataValue}`;
    }

    return `${this.baseUrl}&${eventType}=${eventDataValue}`;
  }

  /**
   * Navigates the sportsbook iframe to the betting rules hash without forcing a full reload
   * when the user is already on the same base document — preserves betslip and in-page state.
   */
  public showBettingRules(): void {
    const BETTING_RULES_IFRAME_HASH = 'betting-rules';

    const { iframeElement } = this.config;

    if (!iframeElement?.contentWindow || !iframeElement.src) {
      return;
    }

    const base = iframeElement.src.split('#')[0];

    iframeElement.contentWindow.location.href = `${base}#${BETTING_RULES_IFRAME_HASH}`;
  }

  /**
   * Go to page via Path Entry.
   * @param payload
   * @returns void
   */
  public sendPostMessage(payload: CactusSportsbook.DeepLink): void {
    const { iframeElement } = this.config;

    if (!iframeElement) {
      return;
    }

    iframeElement.contentWindow?.postMessage(JSON.stringify(payload), '*');
  }

  /**
   * Go to page via Path Entry.
   * @param pathEntry
   * @param callback
   * @returns void
   */
  goToPage(
    pathEntry: CactusSportsbook.PathEntry,
    callback?: (options: GoToPageReturnObject) => void,
  ): void {
    const iframeUrl = this.createRedirectUrl(pathEntry);
    const navigationObject = this.createNavigateObject(pathEntry);

    const { behavior } = this.config;

    if (!callback) {
      if (behavior?.redirect !== 'off') {
        this.redirectTo(iframeUrl);
      }

      if (behavior?.postMessage !== 'off') {
        this.sendPostMessage(navigationObject);
      }
    } else {
      const callbackParams = {
        iframeUrl,
        navigationObject,
      };

      callback(callbackParams);

      if (behavior?.redirect === 'force') {
        this.redirectTo(iframeUrl);
      }

      if (behavior?.postMessage === 'force') {
        this.sendPostMessage(navigationObject);
      }
    }
  }
}
